import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import translation from "~/i18n";
import { useTranslation } from "react-i18next";

// Components
import Form from "~/common/components/Form";
import Input from "~/common/components/Input";
import NumberInput from "~/common/components/NumberInput";
import Select from "~/common/components/Select";

// Assets
import "~/assets/sass/pages/mypage/admin/purchase.scss";
import { loadTossPayments } from "@tosspayments/payment-sdk";
import ModalTemplate from "~/common/components/ModalPortal/ModalTemplate";
import { CardNumberFormatter, thousandFormatter } from "~/utils/formatter";
import { LICENSE_PERIOD } from "~/common/constants/form";
import useAlert from "~/common/hooks/useAlert";
import useRequest from "~/common/hooks/useRequest";
import { WEB_URL } from "~/common/constants/env";
import { useGlobalStore } from "~/store";
import { TOSS_CLIENT_KEY } from "~/common/constants/envKeys";
import { API_STATE } from "~/common/constants/state";
import { CARD_METADATA } from "~/common/constants/payment";

const TOSS_ERROR_CODE = {
	INVALID_CARD_NUMBER() {
		return <h3>카드번호를 다시 확인해주세요.</h3>;
	},
	NOT_SUPPORTED_CARD_TYPE() {
		return <h3>지원되지 않는 카드 종류입니다.</h3>;
	},
	INVALID_CARD_PASSWORD() {
		return <h3>카드 비밀번호를 다시 확인해주세요.</h3>;
	},
	INVALID_CARD_EXPIRATION() {
		return <h3>카드 유효기간을 다시 확인해주세요.</h3>;
	},
	INVALID_CARD_IDENTITY() {
		return <h3>입력하신 주민번호/사업자번호가 카드 소유주 정보와 일치하지 않습니다.</h3>;
	},
	INVALID_REJECT_CARD() {
		return (
			<>
				<h3>카드 사용이 거절되었습니다.</h3>
				<p> 카드사 문의가 필요합니다.</p>
			</>
		);
	},
	INVALID_STOPPED_CARD() {
		return <h3>정지된 카드 입니다. </h3>;
	},
	INVALID_BIRTH_DAY_FORMAT() {
		return (
			<>
				<h3>생년월일 정보는 6자리의 `yyMMdd` 형식이어야 합니다.</h3>
				<p>사업자등록번호는 10자리의 숫자여야 합니다.</p>
			</>
		);
	},
	NOT_REGISTERED_CARD_COMPANY() {
		return <h3>카드를 사용 등록 후 이용해주세요.</h3>;
	},
	INVALID_EMAIL() {
		return <h3>유효하지 않은 이메일 주소 형식입니다.</h3>;
	},
	NOT_SUPPORTED_METHOD() {
		return <h3>지원되지 않는 결제 수단입니다.</h3>;
	},
	INVALID_REQUEST() {
		return (
			<>
				<h3>잘못된 요청입니다.</h3>
				<p>재시도 후 같은 오류 발생 시 채널톡으로 문의해주세요.</p>
			</>
		);
	},
	EXCEED_MAX_AUTH_COUNT() {
		return (
			<>
				<h3>최대 인증 횟수를 초과했습니다.</h3>
				<p>카드사로 문의해주세요.</p>
			</>
		);
	},
	REJECT_CARD_COMPANY() {
		return <h3>결제 승인이 거절되었습니다.</h3>;
	},
	REJECT_ACCOUNT_PAYMENT() {
		return <h3>잔액부족으로 결제에 실패했습니다.</h3>;
	},
	PAY_PROCESS_CANCELED() {
		return <h3>사용자에 의해 결제가 취소되었습니다.</h3>;
	},
	PAY_PROCESS_ABORTED() {
		return <h3>결제 진행 중 승인에 실패하여 결제가 중단되었습니다.</h3>;
	},
	COMMON_ERROR() {
		return (
			<>
				<h3>일시적인 오류가 발생했습니다.</h3>
				<p>잠시 후 다시 시도해주세요.</p>
			</>
		);
	},
};

const Purchase = ({ purchaseInfo, setPage, resetPage, showSuccessAlert, qs }) => {
	const { t } = useTranslation();
	const [{ user }] = useGlobalStore();
	const purchaseForm = useForm({
		defaultValues: { userName: user.info.name, userEmail: user.info.id },
	});

	const customerKeyRequest = useRequest("get", "vc/admin/customerKey");
	const executePurchaseProcessRequest = useRequest("post", "vc/admin/subscription/payment");
	const activatePromotion = useRequest("get", "vc/admin/subscription/activate");
	const defaultCardRequest = useRequest("get", "vc/admin/purchase/option");

	const [showAlert, InfoAlert] = useAlert();
	const [showCheckAlert, CheckAlert] = useAlert();

	const [refundPolicyModal, setRefundPolicyModal] = useState(false);
	const [defaultCard, setDefaultCard] = useState({});

	const navigate = useNavigate();

	const overInvitedUserCount = purchaseInfo.members < 6 ? 6 : purchaseInfo.members;
	const isPromotion = purchaseInfo.promotion ?? true;

	const subscription = async (data) => {
		const tossPayments = await loadTossPayments(TOSS_CLIENT_KEY);
		const customerKey = await customerKeyRequest.asyncCall().then((r) => r.Company.customerKey);

		return await tossPayments.requestBillingAuth("카드", {
			customerKey,
			customerName: user.info.name,
			customerEmail: user.info.id,
			successUrl: `${WEB_URL}myPage/admin/license/success?phone=${data.userPhone}&bizNum=${data.BizNum}&email=${user.info.id}`,
			failUrl: `${WEB_URL}myPage/admin/license/fail`,
		});
	};

	const afterPurchase = () => {
		resetPage();
		setPage("fee");
		showSuccessAlert().then(() => navigate("/myPage/admin/license"));
	};

	const defaultCardCheck = (data) => {
		executePurchaseProcessRequest.call(
			{
				amount: overInvitedUserCount * LICENSE_PERIOD.month,
				phone: data.phone,
				bizNum: data.bizNum,
				mode: "web",
			},
			"body"
		);
	};

	const onSubmit = async (data) => {
		if (Object.keys(defaultCard).length !== 0) {
			showCheckAlert().then(() => defaultCardCheck(data));
			return;
		}
		if (Object.keys(purchaseInfo).length <= 2) {
			console.log("2");
			await subscription(data);
			return;
		}
		if (purchaseInfo.remainder === 1 && isPromotion) {
			console.log("3");
			activatePromotion.call();
			return;
		}
		if (purchaseInfo.remainder === 0 && isPromotion) {
			console.log("4");
			await subscription(data);
			return;
		}
		await subscription(data);
	};

	const checkQueryString = () => {
		Object.keys(qs).length !== 0 &&
			showAlert().then(() => {
				navigate("/myPage/admin/license");
			});
	};

	useEffect(() => {
		checkQueryString();
	}, []);

	useEffect(() => {
		defaultCardRequest.call();
	}, []);

	useEffect(() => {
		switch (defaultCardRequest.state) {
			case API_STATE.done:
				const response = defaultCardRequest.response.data;
				setDefaultCard(response.find((card) => card.default === 1) ?? {});
				break;
		}
	}, [defaultCardRequest.state]);

	useEffect(() => {
		switch (executePurchaseProcessRequest.state) {
			case API_STATE.done:
				afterPurchase();
				break;
			case API_STATE.error:
				navigate("/404");
				break;
		}
	}, [executePurchaseProcessRequest.state]);

	useEffect(() => {
		switch (activatePromotion.state) {
			case API_STATE.done:
				afterPurchase();
				break;
			case API_STATE.error:
				navigate("/404");
				break;
		}
	}, [activatePromotion.state]);

	return (
		<>
			<dl className="purchase-top-msg">
				<dt>{t("pages.myPage.admin.LicensePage.purchase.purchase-top-msg.dt")}</dt>
				<dd>{t("pages.myPage.admin.LicensePage.purchase.purchase-top-msg.dd")}</dd>
			</dl>
			<div className="c__card purchase-card">
				<section className="purchase-info">
					<div className="column">
						<h2 className="form-title">
							{t("pages.myPage.admin.LicensePage.purchase.purchase-info.form-title")}
						</h2>
						<Form useForm={purchaseForm} className="purchase-form vertical">
							<Input
								label={t(
									"pages.myPage.admin.LicensePage.purchase.purchase-info.InputNameLabel"
								)}
								name="userName"
								placeholder={t(
									"pages.myPage.admin.LicensePage.purchase.purchase-info.InputNamePlaceholder"
								)}
								readonly
							/>
							<Input
								label={t(
									"pages.myPage.admin.LicensePage.purchase.purchase-info.InputEmailLabel"
								)}
								name="userEmail"
								placeholder={t(
									"pages.myPage.admin.LicensePage.purchase.purchase-info.InputEmailPlaceholder"
								)}
								readonly
							/>
							<NumberInput
								label={t(
									"pages.myPage.admin.LicensePage.purchase.purchase-info.InputNumberLabel"
								)}
								name="userPhone"
								placeholder="ex) 010-1234-5678"
								thousandSeparator={false}
								type={"phone"}
								required
							/>
							<Input
								label={t(
									"pages.myPage.admin.LicensePage.purchase.purchase-info.InputBizNumLabel"
								)}
								name="BizNum"
								placeholder={t(
									"pages.myPage.admin.LicensePage.purchase.purchase-info.InputBizNumPlaceholder"
								)}
								validation={{
									minLength: {
										value: 6,
										message: "6자리 혹은 10자리 숫자를 입력해주세요",
									},
									maxLength: {
										value: 10,
										message: "6자리 혹은 10자리 숫자를 입력해주세요",
									},
								}}
								required
							/>
						</Form>
					</div>
					<div className="refund-policy">
						<p className="refund-content">
							<p className="refund-title pre">환불정책을 확인해주세요!</p>
							<p className="refund-content pre">
								<b>
									사용 이력이 없고 결제 일로부터 7일이 지나지 않은경우 100% 환불이
									가능
								</b>
								합니다.
								<br />
								7일이 지났거나 이용 내역이 있을시에는 해당 월 요금을 납부하게
								됩니다.
							</p>
						</p>
						<button
							type="button"
							className="btn-detail"
							onClick={() => setRefundPolicyModal(true)}
						>
							자세히 보기
						</button>
					</div>
				</section>
				<section className="purchase-confirm ir-consulting">
					<h2 className="form-title">결제내역</h2>
					<dl className="buy-product">
						<dt>
							{t(
								"pages.myPage.admin.LicensePage.purchase.purchase-confirm.buy-product.dt"
							)}
						</dt>
						<dd>
							{t(
								"pages.myPage.admin.LicensePage.purchase.purchase-confirm.buy-product.dd"
							)}{" "}
							- Enterprise
						</dd>
					</dl>
					<Form useForm={purchaseForm} className="confirm-form vertical">
						<Select
							label={t(
								"pages.myPage.admin.LicensePage.purchase.purchase-confirm.SelectLabel"
							)}
							name="itemOption"
							options={[
								{
									label: translation.t(
										"pages.myPage.admin.LicensePage.purchase.purchase-confirm.SelectOption1"
									),
									value: "정기결제(월결제)",
								},
							]}
							defaultValue="정기결제(월결제)"
							required
						/>
						<div className="f__group">
							<div className="f__label">결제 금액</div>
							<div className="f__control">
								<dl className="item-sum">
									<div className="item-top">
										<div>
											<dt>총 결제 금액</dt>
											<dd>
												<b>
													{thousandFormatter(
														LICENSE_PERIOD.month.money *
															overInvitedUserCount
													)}
												</b>{" "}
												원
											</dd>
										</div>
									</div>
									<div className="item-bottom">
										<div>
											<dt>
												└ Pro{" "}
												{thousandFormatter(LICENSE_PERIOD.month.money)}원
												(월) 초대된 팀원 수 {purchaseInfo.members}명 )
											</dt>
											<dd>
												{thousandFormatter(
													LICENSE_PERIOD.month.money *
														overInvitedUserCount
												)}{" "}
												원
											</dd>
										</div>
										<div>
											<dt>└ 할인 된 금액</dt>
											<dd>
												-{" "}
												{isPromotion
													? thousandFormatter(
															LICENSE_PERIOD.month.money *
																overInvitedUserCount
													  )
													: "0"}{" "}
												원
											</dd>
										</div>
									</div>
								</dl>
								<dl className="price">
									<dt>최종 결제 금액</dt>
									<dd>
										<b>
											{isPromotion
												? 0
												: thousandFormatter(
														LICENSE_PERIOD.month.money *
															overInvitedUserCount
												  )}
										</b>{" "}
										원
									</dd>
								</dl>
							</div>
						</div>
					</Form>
					<div className="purchase-agree">
						<div className="f__group">
							<div
								className={`f__control ${
									purchaseForm.formState.errors.agree ? "has-error" : ""
								}`}
							>
								<div className="checkbox">
									<label className={`checkbox-custom`}>
										<input
											type="checkbox"
											{...purchaseForm.register("agree", {
												required: true,
											})}
										/>
										<span className="checkmark" />
										<span className="checkbox-label">
											{t(
												"pages.myPage.admin.LicensePage.purchase.purchase-confirm.checkbox-label"
											)}
										</span>
									</label>
								</div>
							</div>
						</div>
					</div>
					<div className="btn-wrap">
						<button
							type="button"
							className="btn__solid purchase-btn"
							color="gray"
							onClick={() => setPage("fee")}
						>
							취소
						</button>
						<button
							type="button"
							className="btn__solid purchase-btn"
							color="primary"
							onClick={purchaseForm.handleSubmit(onSubmit)}
						>
							{t("common.commonWord.button.paying")}
						</button>
					</div>
				</section>
			</div>

			{Object.keys(qs).length !== 0 && (
				<InfoAlert
					type="info"
					confirm={"확인"}
					width={430}
					height={250}
					message={TOSS_ERROR_CODE[qs.code ?? "COMMON_ERROR"]()}
				/>
			)}

			{defaultCard && Object.keys(defaultCard).length !== 0 && (
				<CheckAlert
					type="info"
					confirm={"확인"}
					reject={"취소"}
					width={430}
					height={250}
					message={
						<>
							<h3>기본 결제수단으로 결제합니다.</h3>
							<p>{`${CARD_METADATA[defaultCard.acquirerCode].name ?? ""}(${
								defaultCard.cardType ?? ""
							}) ${CardNumberFormatter(defaultCard.cardNumber)}`}</p>
						</>
					}
				/>
			)}
			<ModalTemplate
				title="환불정책"
				modalState={refundPolicyModal}
				setModalToggle={setRefundPolicyModal}
				width={574}
				className="refund-policy-modal"
			>
				<div className="modal-content">
					<p className="refund-phrase">
						<b> 사용이력이 없고 결제일로부터 7일 지나지 않은 경우 100% 환불 가능</b>{" "}
						합니다.
						<br />
						7일이 지났거나 이용 내역이 있을 시 해당 월 요금을 납부하게 됩니다.
					</p>
					<div className="desc-box">
						<div className="refund-item">
							1. 회원은 회사에 환불을 요구할 수 있습니다. 환불은 회사가 안내하는 정책
							및 방법에 따라 진행됩니다.
						</div>

						<div className="refund-item">
							<p>2. 회사는 다음 각 호의 방식으로 환불을 진행합니다.</p>
							<dl>
								<dt>
									가. <b>월간 결제 회원</b>
								</dt>
								<dd>
									결제 시간으로부터 7일까지는 전액 할부가 가능합니다. 7일이후 30일
									이내 사용자는 환불이 불가능 합니다.
								</dd>
							</dl>
						</div>
					</div>
				</div>
				<div className="modal-footer">
					<button
						className="btn__solid"
						color="gray"
						onClick={() => setRefundPolicyModal(false)}
					>
						취소
					</button>
					<button
						className="btn__solid"
						color="primary"
						onClick={() => setRefundPolicyModal(false)}
					>
						확인
					</button>
				</div>
			</ModalTemplate>
		</>
	);
};
export default Purchase;
